import { router, usePage } from "@inertiajs/react";
import { Search } from "lucide-react";
import { useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { debounce, populateQueryParams } from "@/lib/utils";
import type { TableToolbarDataProps } from "@/pages/AdminPortal/Service/Index";
import type { GlobalPageProps } from "@/types/globals";

export interface ToolbarTableProps {
	table: TableToolbarDataProps;
}

export default function ToolbarTable({ table: _ }: ToolbarTableProps) {
	const { url: pageURL, props: globalProps } = usePage<GlobalPageProps>();

	const [filterBy, setFilterBy] = useState({
		name: globalProps?.adminPortal?.currentQuery?.name || "",
		filterByStatus:
			globalProps?.adminPortal?.currentQuery?.filterByStatus || "",
		specialTier: globalProps?.adminPortal?.currentQuery?.specialTier || false,
	});
	const updateQueryParams = useCallback(
		debounce((value) => {
			const { fullUrl, queryParams } = populateQueryParams(pageURL, {
				...value,
			});

			router.get(
				fullUrl,
				{ ...queryParams },
				{ preserveState: true, only: ["services"] },
			);
		}, 500),
		[],
	);
	const handleFilterBy = ({
		value,
		type,
	}: {
		value: string | boolean;
		type: keyof typeof filterBy;
	}) => {
		setFilterBy({ ...filterBy, [type]: value });
		updateQueryParams(deepTransformKeysToSnakeCase({ [type]: value }));
	};

	return (
		<section className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-2 lg:grid-cols-4 xl:grid-cols-5">
			<div className="col-span-full md:col-span-2">
				<Input
					value={filterBy.name}
					StartIcon={{ icon: Search }}
					type="text"
					placeholder="Filter by brand name..."
					className="bg-input shadow-inner"
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({
							type: "name",
							value: event.target.value,
						});
					}}
				/>
			</div>

			<div className="md:col-span-1">
				<Select
					value={filterBy.filterByStatus}
					onValueChange={(value) =>
						handleFilterBy({
							type: "filterByStatus",
							value,
						})
					}
				>
					<SelectTrigger className="w-full bg-input shadow-inner">
						<SelectValue placeholder="All Status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Status</SelectItem>
						<SelectItem value="active">Active</SelectItem>
						<SelectItem value="inactive">Inactive</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div className="flex items-center gap-2 md:col-span-1">
				<Switch
					id="special-tier"
					checked={!!filterBy.specialTier}
					onCheckedChange={(checked) =>
						handleFilterBy({
							type: "specialTier",
							value: !!checked,
						})
					}
				/>
				<Label htmlFor="special-tier" className="text-sm cursor-pointer">
					Special Tier Only
				</Label>
			</div>
		</section>
	);
}
